import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ServiceOrderCard = ({ order, onStatusUpdate, onTimeLog, onViewDetails }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Pendente':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'Em Andamento':
        return 'bg-primary/10 text-primary border-primary/20';
      case 'Concluído':
        return 'bg-success/10 text-success border-success/20';
      case 'Cancelado':
        return 'bg-error/10 text-error border-error/20';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Alta':
        return 'text-error';
      case 'Média':
        return 'text-warning';
      case 'Baixa':
        return 'text-success';
      default:
        return 'text-muted-foreground';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    return new Date(dateString)?.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-professional hover:shadow-modal transition-all duration-200">
      {/* Card Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2 mb-1">
              <h3 className="font-semibold text-card-foreground text-lg">
                OS #{order?.number}
              </h3>
              <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(order?.status)}`}>
                {order?.status}
              </span>
            </div>
            <p className="text-sm text-muted-foreground mb-2">
              {order?.client?.name}
            </p>
            <div className="flex items-center space-x-4 text-xs text-muted-foreground">
              <span className="flex items-center">
                <Icon name="Calendar" size={14} className="mr-1" />
                {formatDate(order?.createdAt)}
              </span>
              <span className="flex items-center">
                <Icon name="Clock" size={14} className="mr-1" />
                {formatTime(order?.createdAt)}
              </span>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex-shrink-0"
          >
            <Icon name={isExpanded ? "ChevronUp" : "ChevronDown"} size={20} />
          </Button>
        </div>

        {/* Priority and Service Type */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <span className="flex items-center text-sm">
              <Icon name="AlertCircle" size={16} className={`mr-1 ${getPriorityColor(order?.priority)}`} />
              <span className={getPriorityColor(order?.priority)}>{order?.priority}</span>
            </span>
            <span className="text-sm text-muted-foreground">
              {order?.serviceType}
            </span>
          </div>
          {order?.assignedTechnician && (
            <div className="flex items-center space-x-2">
              <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                <span className="text-xs text-primary-foreground font-medium">
                  {order?.assignedTechnician?.name?.charAt(0)}
                </span>
              </div>
              <span className="text-sm text-muted-foreground hidden sm:inline">
                {order?.assignedTechnician?.name}
              </span>
            </div>
          )}
        </div>
      </div>
      {/* Expanded Content */}
      {isExpanded && (
        <div className="p-4 space-y-4">
          {/* Description */}
          <div>
            <h4 className="text-sm font-medium text-card-foreground mb-2">Descrição</h4>
            <p className="text-sm text-muted-foreground">
              {order?.description}
            </p>
          </div>

          {/* Client Details */}
          <div>
            <h4 className="text-sm font-medium text-card-foreground mb-2">Cliente</h4>
            <div className="space-y-1 text-sm text-muted-foreground">
              <p className="flex items-center">
                <Icon name="Building" size={14} className="mr-2" />
                {order?.client?.name}
              </p>
              <p className="flex items-center">
                <Icon name="MapPin" size={14} className="mr-2" />
                {order?.client?.address}
              </p>
              <p className="flex items-center">
                <Icon name="Phone" size={14} className="mr-2" />
                {order?.client?.phone}
              </p>
            </div>
          </div>

          {/* Time Tracking */}
          {order?.timeTracking && (
            <div>
              <h4 className="text-sm font-medium text-card-foreground mb-2">Tempo Trabalhado</h4>
              <div className="flex items-center space-x-4 text-sm">
                <span className="flex items-center text-muted-foreground">
                  <Icon name="Clock" size={14} className="mr-1" />
                  {order?.timeTracking?.totalHours}h {order?.timeTracking?.totalMinutes}min
                </span>
                {order?.timeTracking?.isActive && (
                  <span className="flex items-center text-success">
                    <div className="w-2 h-2 bg-success rounded-full mr-2 animate-pulse"></div>
                    Ativo
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Notes */}
          {order?.notes && order?.notes?.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-card-foreground mb-2">Observações</h4>
              <div className="space-y-2">
                {order?.notes?.slice(0, 2)?.map((note, index) => (
                  <div key={index} className="text-sm text-muted-foreground">
                    <p className="font-medium">{note?.author} - {formatDate(note?.createdAt)}</p>
                    <p>{note?.content}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {/* Action Buttons */}
      <div className="p-4 border-t border-border">
        <div className="flex flex-wrap gap-2">
          {order?.status === 'Pendente' && (
            <Button
              variant="default"
              size="sm"
              onClick={() => onStatusUpdate(order?.id, 'Em Andamento')}
              iconName="Play"
              iconPosition="left"
              className="flex-1 sm:flex-none"
            >
              Iniciar
            </Button>
          )}
          
          {order?.status === 'Em Andamento' && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onTimeLog(order?.id)}
                iconName="Clock"
                iconPosition="left"
                className="flex-1 sm:flex-none"
              >
                Tempo
              </Button>
              <Button
                variant="success"
                size="sm"
                onClick={() => onStatusUpdate(order?.id, 'Concluído')}
                iconName="Check"
                iconPosition="left"
                className="flex-1 sm:flex-none"
              >
                Concluir
              </Button>
            </>
          )}

          <Button
            variant="ghost"
            size="sm"
            onClick={() => onViewDetails(order?.id)}
            iconName="Eye"
            iconPosition="left"
            className="flex-1 sm:flex-none"
          >
            Detalhes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ServiceOrderCard;